/* ===================================================================
   Display helpers -- map live api.ts types into the screens' UI shapes
   without changing the design (Hopper, 2026-06-09).

   The Claude Design screens render a ReviewItem / customer row shape with
   an avatar color, an initial, and a friendly "2h ago" timestamp. The
   live API returns raw rows. These pure helpers bridge the two so the
   screens stay pixel-faithful while reading live data.
   =================================================================== */
import type { Review, Customer } from './api';
import { ApiError } from './api';
import type { ReviewItem, ChipKind } from '../components/ui';

/* Map a raw backend error code to a plain, owner-friendly sentence (U8). The
   original code is NOT shown inline; callers can expose it behind an
   expandable "Details" if needed. Unknown codes fall back to a generic line. */
const _ERROR_MESSAGES: Record<string, string> = {
  invalid_api_key: 'We could not reach your account data. Please refresh, or sign out and back in.',
  unauthorized: 'Your session expired. Please sign in again.',
  forbidden: 'You do not have access to that.',
  not_found: 'We could not find that.',
  rate_limited: 'Too many requests just now. Please wait a moment and try again.',
  network_error: 'We could not connect. Check your internet and try again.',
  internal_error: 'Something went wrong on our end. Please try again in a moment.',
  temporarily_unavailable: 'That is temporarily unavailable. Please try again shortly.',
};

export function friendlyError(
  raw: string | null | undefined,
  fallback = 'Something went wrong. Please try again.',
): string {
  if (!raw) return fallback;
  const key = raw.trim().toLowerCase();
  return _ERROR_MESSAGES[key] ?? fallback;
}

/* Map a thrown ApiError to an HONEST, actionable sentence by HTTP STATUS, so a
   catch block can distinguish a dead session (sign in again) from a server
   hiccup (retry) from bad input -- instead of a one-size "try again in a
   moment" that hides an auth failure. Pair with the transparent 401 re-mint in
   lib/api/_http.ts: by the time a 401 actually surfaces here, recovery already
   failed (the Supabase session is gone and the user is being signed out), so
   "session expired, sign in again" is the truthful thing to show. A non-ApiError
   throwable falls back to the caller's message. */
export function apiErrorMessage(
  e: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return _ERROR_MESSAGES.unauthorized;
    if (e.status === 0) return _ERROR_MESSAGES.network_error;
    if (e.status === 429) return _ERROR_MESSAGES.rate_limited;
    if (e.status >= 500) return _ERROR_MESSAGES.internal_error;
    // Known backend error codes (e.g. invalid_api_key) still map nicely.
    return friendlyError(e.message, fallback);
  }
  return fallback;
}

/* Time-of-day greeting (U12). The prototype hard-coded "Good morning" at all
   hours; compute the real daypart from the local clock instead. */
export function daypartGreeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/* Deterministic avatar palette (same hues the mock used). Picking by a
   stable hash of the name keeps a given person the same color run-to-run. */
const AVATAR_COLORS = [
  '#2A6FDB', '#1F8A5B', '#7A3FB5', '#B5532A',
  '#2A8FA0', '#8A6BB5', '#3F8FB5', '#C1923E',
];

export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export function initialOf(name: string): string {
  const t = (name || '').trim();
  return t ? t[0].toUpperCase() : '?';
}

/* "2h ago" / "3d ago" / "Jun 2" style relative time from an ISO string. */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* Label for a review's stored platform. DEFENSIVE ONLY -- it does NOT mean
   Rhody Reviews ingests from these. Review ingestion is Google-only (the
   monitor agent hard-refuses yelp and facebook), so `google` is the only value
   the backend can write today. The other two stay here because the reviews
   table CHECK still permits them and a legacy row must render with a real name
   rather than a raw slug. Nothing in the UI OFFERS yelp or facebook as a
   source. */
const SOURCE_LABEL: Record<Review['platform'], string> = {
  google: 'Google',
  yelp: 'Yelp',
  facebook: 'Facebook',
};

/* Map a live Review -> the ReviewItem the design's ReviewCard expects.

   The shown reply text is, in priority order: an explicit `draft` override,
   the pending AI draft folded in from the outbox (r.draft_response), or the
   already-posted reply (r.reply_text). A needs-reply review now carries its
   pending AI draft inline; a replied review shows the posted reply. */
export function toReviewItem(r: Review, draft?: string): ReviewItem {
  return {
    id: r.review_id,
    // The row's DB uuid -- the manual-reply endpoint keys on this, not review_id.
    rowId: r.id,
    initial: initialOf(r.reviewer_name),
    who: r.reviewer_name || 'Anonymous',
    color: avatarColor(r.reviewer_name || r.review_id),
    stars: r.rating,
    source: SOURCE_LABEL[r.platform] ?? r.platform,
    when: relativeTime(r.review_date),
    text: r.review_text || '',
    draft: draft ?? r.draft_response ?? r.reply_text ?? undefined,
    escalated: Boolean(r.escalated),
  };
}

export function fullName(c: Customer): string {
  return [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || 'Customer';
}

export function contactLine(c: Customer): string {
  return c.email || c.phone || '';
}

/* Status chip for a customer row, now driven by the real review-join
   (reviews_left from GET /customers) on top of do_not_contact +
   relationship_type. "Reviewed" = has left at least one matched review;
   "Not yet asked" = none yet. */
export function customerStatus(c: Customer): { label: string; kind: ChipKind } {
  if (c.do_not_contact) return { label: 'Do not contact', kind: 'red' };
  if ((c.reviews_left ?? 0) > 0) return { label: 'Reviewed', kind: 'green' };
  if (c.relationship_type === 'past_advertiser') return { label: 'Win-back', kind: 'gold' };
  return { label: 'Not yet asked', kind: 'gray' };
}
